import React, { useState } from 'react';
import { mockData } from '../mockData';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Globe, MapPin, Phone, Mail, Clock, Star, ChefHat, Pizza, Sandwich, Salad, UtensilsCrossed, Truck, Instagram, Facebook, Beef } from 'lucide-react';

const Home = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [selectedSize, setSelectedSize] = useState({});

  const handleSizeSelect = (pizzaId, size) => {
    setSelectedSize(prev => ({ ...prev, [pizzaId]: size }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-neutral-950/80 backdrop-blur-xl border-b border-amber-900/20">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_d541c676-9200-4c85-b009-646509011cd2/artifacts/az877vf0_image.png" 
                alt="Back & Snacks Logo" 
                className="h-16 w-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-serif font-bold text-amber-500">Back & Snacks</h1>
                <p className="text-xs text-amber-300/60">Seit 2021</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-amber-100/80 hover:text-amber-400 transition-colors text-sm font-medium">{language === 'de' ? 'Über uns' : 'About'}</a>
              <a href="#bowls" className="text-amber-100/80 hover:text-amber-400 transition-colors text-sm font-medium">Bowls</a>
              <a href="#pizzas" className="text-amber-100/80 hover:text-amber-400 transition-colors text-sm font-medium">Pizzas</a>
              <a href="#baguettes" className="text-amber-100/80 hover:text-amber-400 transition-colors text-sm font-medium">Baguettes</a>
              <a href="#burgers" className="text-amber-100/80 hover:text-amber-400 transition-colors text-sm font-medium">Burgers</a>
              <a href="#salads" className="text-amber-100/80 hover:text-amber-400 transition-colors text-sm font-medium">{language === 'de' ? 'Salate' : 'Salads'}</a>
              <a href="#mittagessen" className="text-amber-100/80 hover:text-amber-400 transition-colors text-sm font-medium">Mittagessen</a>
              <a href="#contact" className="text-amber-100/80 hover:text-amber-400 transition-colors text-sm font-medium">Contact</a>
            </div>

            <Button 
              onClick={toggleLanguage} 
              variant="outline" 
              size="sm"
              className="border-amber-700/50 bg-amber-900/20 hover:bg-amber-900/40 text-amber-200"
            >
              <Globe className="h-4 w-4 mr-2" />
              {language === 'de' ? 'EN' : 'DE'}
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(217,119,6,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(245,158,11,0.05),transparent_50%)]" />
        
        <div className="container mx-auto text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-6xl md:text-8xl font-serif font-bold text-amber-400 mb-6 tracking-tight">
              Back & Snacks
            </h2>
            <p className="text-2xl md:text-3xl text-amber-100/90 mb-4 font-light">
              {language === 'de' ? 'Frisches Essen. Ehrlicher Geschmack.' : 'Fresh Food. Honest Taste.'}
            </p>
            <p className="text-xl text-amber-200/70 mb-12">
              {language === 'de' ? 'Täglich frisch zubereitet' : 'Made fresh daily'}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white shadow-lg shadow-amber-900/50 transition-all duration-300 hover:shadow-xl hover:shadow-amber-900/60 hover:scale-105"
                onClick={() => document.getElementById('bowls').scrollIntoView({ behavior: 'smooth' })}
              >
                <UtensilsCrossed className="mr-2 h-5 w-5" />
                {language === 'de' ? 'Speisekarte ansehen' : 'View Menu'}
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-amber-600/50 bg-amber-950/30 hover:bg-amber-950/50 text-amber-200 transition-all duration-300 hover:scale-105"
                onClick={() => window.open(mockData.deliveryPartners[0].url, '_blank')}
              >
                <Truck className="mr-2 h-5 w-5" />
                {language === 'de' ? 'Jetzt bestellen' : 'Order Now'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-gradient-to-b from-transparent to-neutral-900/50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h3 className="text-4xl font-serif font-bold text-amber-400 mb-6">
                {language === 'de' ? 'Willkommen bei Back & Snacks' : 'Welcome to Back & Snacks'}
              </h3>
              <p className="text-amber-100/80 text-lg leading-relaxed mb-6">
                {language === 'de' 
                  ? 'Back and Snacks ist ein modernes Nachbarschaftsrestaurant in Bremen, das frisch zubereitete Mahlzeiten mit hochwertigen Zutaten anbietet. Wir konzentrieren uns auf ehrliches Essen, wöchentlich wechselnde Mittagsmenüs und Gerichte, die jeder liebt.'
                  : 'Back and Snacks is a modern neighborhood restaurant in Bremen, offering freshly prepared meals with quality ingredients. We focus on honest food, weekly changing lunch menus, and dishes everyone loves.'
                }
              </p>
              <div className="flex items-center gap-3 text-amber-200/70 mb-4">
                <MapPin className="h-5 w-5 text-amber-500" />
                <span>{language === 'de' ? 'Stolz in Bremen verwurzelt' : 'Proudly rooted in Bremen'}</span>
              </div>
              <div className="flex items-center gap-3 text-amber-200/70 mb-6">
                <ChefHat className="h-5 w-5 text-amber-500" />
                <span>{language === 'de' ? 'Geführt von Prasanna Kummara' : 'Owned & managed by Prasanna Kummara'}</span>
              </div>
              <p className="text-sm text-amber-300/60 italic">
                {language === 'de' 
                  ? '"Qualität und Frische in jedem Bissen – das ist unser Versprechen an Sie."'
                  : '"Quality and freshness in every bite – that is our promise to you."'
                }
              </p>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-amber-900/30">
                <img 
                  src="https://images.unsplash.com/photo-1590754797999-7d6ecd8df8a8?w=800" 
                  alt="Bremen Historic Architecture" 
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/60 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bowls Section */}
      <section id="bowls" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <ChefHat className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-5xl font-serif font-bold text-amber-400 mb-4">Bowls</h3>
            <p className="text-amber-200/70 text-lg max-w-2xl mx-auto">
              {language === 'de' 
                ? 'Gesunde, nährstoffreiche Bowls mit frischen Zutaten und authentischen Aromen'
                : 'Healthy, nutritious bowls with fresh ingredients and authentic flavors'
              }
            </p>
            <p className="text-amber-300/60 text-sm mt-2">
              {language === 'de' 
                ? 'Alle Bowls serviert mit Dressing nach Wahl und frischem Brötchen'
                : 'All bowls served with dressing of your choice and fresh bread roll'
              }
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mockData.bowls.map((bowl) => (
              <Card key={bowl.id} className="bg-neutral-900/60 backdrop-blur-sm border-amber-900/30 hover:border-amber-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-900/30 group overflow-hidden">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={bowl.image} 
                    alt={t(bowl.name)} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 right-3 flex gap-2">
                    {bowl.tags[language].map((tag, idx) => (
                      <Badge key={idx} className="bg-amber-600/90 text-white border-0 backdrop-blur-sm">{tag}</Badge>
                    ))}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h4 className="text-xl font-semibold text-amber-300 mb-2">{t(bowl.name)}</h4>
                  <p className="text-amber-100/60 text-sm mb-4 line-clamp-2">{t(bowl.description)}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-amber-400">€{bowl.price.toFixed(2)}</span>
                    <Button 
                      size="sm" 
                      className="bg-amber-600 hover:bg-amber-500 text-white transition-colors"
                    >
                      {language === 'de' ? 'Wählen' : 'Select'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pizzas Section */}
      <section id="pizzas" className="py-20 px-4 bg-gradient-to-b from-transparent to-neutral-900/50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Pizza className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-5xl font-serif font-bold text-amber-400 mb-4">Pizzas</h3>
            <p className="text-amber-200/70 text-lg max-w-2xl mx-auto">
              {language === 'de' 
                ? 'Ofenfrische Pizzen mit authentischen Zutaten und knusprigem Boden'
                : 'Oven-fresh pizzas with authentic ingredients and crispy crust'
              }
            </p>
          </div>
          <Accordion type="single" collapsible className="max-w-5xl mx-auto space-y-4">
            {mockData.pizzas.map((pizza) => (
              <AccordionItem 
                key={pizza.id} 
                value={`pizza-${pizza.id}`}
                className="bg-neutral-900/60 backdrop-blur-sm border border-amber-900/30 rounded-xl overflow-hidden hover:border-amber-600/50 transition-all"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline group">
                  <div className="flex items-center gap-4 w-full">
                    <img 
                      src={pizza.image} 
                      alt={t(pizza.name)} 
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                    <div className="flex-1 text-left">
                      <h4 className="text-xl font-semibold text-amber-300 group-hover:text-amber-400 transition-colors">{t(pizza.name)}</h4>
                      <p className="text-amber-100/60 text-sm">{t(pizza.description)}</p>
                    </div>
                    <div className="flex gap-2">
                      {pizza.tags[language].map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-amber-900/40 text-amber-200 border-0">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="flex flex-wrap gap-4 pt-4 border-t border-amber-900/20">
                    {Object.entries(pizza.prices).map(([size, price]) => (
                      <Button
                        key={size}
                        variant={selectedSize[pizza.id] === size ? 'default' : 'outline'}
                        className={selectedSize[pizza.id] === size 
                          ? 'bg-amber-600 hover:bg-amber-500 text-white' 
                          : 'border-amber-700/50 bg-amber-950/20 hover:bg-amber-950/40 text-amber-200'
                        }
                        onClick={() => handleSizeSelect(pizza.id, size)}
                      >
                        {size} - €{price.toFixed(2)}
                      </Button>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Baguettes Section */}
      <section id="baguettes" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Sandwich className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-5xl font-serif font-bold text-amber-400 mb-4">Baguettes</h3>
            <p className="text-amber-200/70 text-lg max-w-2xl mx-auto">
              {language === 'de' 
                ? 'Frisch belegte Baguettes mit knusprigem Brot und erstklassigen Zutaten'
                : 'Freshly filled baguettes with crispy bread and premium ingredients'
              }
            </p>
            <p className="text-amber-300/60 text-sm mt-2">
              {language === 'de' 
                ? 'Alle Baguettes serviert mit Gouda, Tomaten, Gurken und Salat'
                : 'All baguettes served with Gouda cheese, tomatoes, cucumber, and salad'
              }
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.baguettes.map((baguette) => (
              <Card key={baguette.id} className="bg-neutral-900/60 backdrop-blur-sm border-amber-900/30 hover:border-amber-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-900/30 group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={baguette.image} 
                    alt={t(baguette.name)} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 right-3 flex gap-2">
                    {baguette.tags[language].map((tag, idx) => (
                      <Badge key={idx} className="bg-amber-600/90 text-white border-0 backdrop-blur-sm">{tag}</Badge>
                    ))}
                  </div>
                </div>
                <CardContent className="p-5">
                  <h4 className="text-lg font-semibold text-amber-300 mb-2">{t(baguette.name)}</h4>
                  <p className="text-amber-100/60 text-sm mb-4 line-clamp-2">{t(baguette.description)}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-amber-400">€{baguette.price.toFixed(2)}</span>
                    <Button 
                      size="sm" 
                      className="bg-amber-600 hover:bg-amber-500 text-white transition-colors"
                    >
                      {language === 'de' ? 'Wählen' : 'Select'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Burgers Section */}
      <section id="burgers" className="py-20 px-4 bg-gradient-to-b from-transparent to-neutral-900/50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Beef className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-5xl font-serif font-bold text-amber-400 mb-4">Burgers</h3>
            <p className="text-amber-200/70 text-lg max-w-2xl mx-auto">
              {language === 'de' 
                ? 'Saftige Burger mit frischen Zutaten und knusprigen Brötchen'
                : 'Juicy burgers with fresh ingredients and crispy buns'
              }
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.burgers.map((burger) => (
              <Card key={burger.id} className="bg-neutral-900/60 backdrop-blur-sm border-amber-900/30 hover:border-amber-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-900/30 group overflow-hidden">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={burger.image} 
                    alt={t(burger.name)} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 right-3 flex gap-2">
                    {burger.tags[language].map((tag, idx) => (
                      <Badge key={idx} className="bg-amber-600/90 text-white border-0 backdrop-blur-sm">{tag}</Badge>
                    ))}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h4 className="text-xl font-semibold text-amber-300 mb-2">{t(burger.name)}</h4>
                  <p className="text-amber-100/60 text-sm mb-4 line-clamp-2">{t(burger.description)}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-amber-400">€{burger.price.toFixed(2)}</span>
                    <Button 
                      size="sm" 
                      className="bg-amber-600 hover:bg-amber-500 text-white transition-colors"
                    >
                      {language === 'de' ? 'Wählen' : 'Select'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Salads Section */}
      <section id="salads" className="py-20 px-4 bg-gradient-to-b from-transparent to-neutral-900/50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Salad className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-5xl font-serif font-bold text-amber-400 mb-4">{language === 'de' ? 'Salate' : 'Salads'}</h3>
            <p className="text-amber-200/70 text-lg max-w-2xl mx-auto">
              {language === 'de' 
                ? 'Knackige, frische Salate mit hochwertigen Dressings'
                : 'Crisp, fresh salads with premium dressings'
              }
            </p>
            <p className="text-amber-300/60 text-sm mt-2">
              {language === 'de' 
                ? 'Alle Salate serviert mit Dressing nach Wahl und frischem Brötchen'
                : 'All salads served with dressing of your choice and fresh bread roll'
              }
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {mockData.salads.map((salad) => (
              <Card key={salad.id} className="bg-neutral-900/60 backdrop-blur-sm border-amber-900/30 hover:border-amber-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-900/30 group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={salad.image} 
                    alt={t(salad.name)} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 right-3 flex gap-2">
                    {salad.tags[language].map((tag, idx) => (
                      <Badge key={idx} className="bg-amber-600/90 text-white border-0 backdrop-blur-sm">{tag}</Badge>
                    ))}
                  </div>
                </div>
                <CardContent className="p-5">
                  <h4 className="text-lg font-semibold text-amber-300 mb-2">{t(salad.name)}</h4>
                  <p className="text-amber-100/60 text-sm mb-4 line-clamp-2">{t(salad.description)}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-amber-400">€{salad.price.toFixed(2)}</span>
                    <Button 
                      size="sm" 
                      className="bg-amber-600 hover:bg-amber-500 text-white transition-colors"
                    >
                      {language === 'de' ? 'Wählen' : 'Select'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Dressings */}
          <div className="max-w-4xl mx-auto">
            <h4 className="text-2xl font-serif font-bold text-amber-400 mb-6 text-center">{language === 'de' ? 'Verfügbare Dressings' : 'Available Dressings'}</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {mockData.dressings.map((dressing, idx) => (
                <div key={idx} className="bg-neutral-900/40 backdrop-blur-sm border border-amber-900/30 rounded-lg p-4 text-center hover:border-amber-600/50 transition-all hover:scale-105">
                  <div className="h-10 w-10 rounded-full bg-amber-900/30 flex items-center justify-center mx-auto mb-2">
                    <span className="text-amber-400 text-xl">💧</span>
                  </div>
                  <p className="text-amber-200/80 text-sm font-medium">{t(dressing.name)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mittagessen Section */}
      <section id="mittagessen" className="py-20 px-4 bg-gradient-to-b from-neutral-900/50 to-amber-950/20">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-gradient-to-br from-amber-900/30 to-amber-950/20 backdrop-blur-xl border-2 border-amber-600/40 rounded-3xl p-12 shadow-2xl shadow-amber-900/30">
            <div className="text-center mb-12">
              <div className="inline-block p-4 bg-amber-900/40 rounded-full mb-4">
                <UtensilsCrossed className="h-12 w-12 text-amber-400" />
              </div>
              <h3 className="text-5xl font-serif font-bold text-amber-400 mb-4">{t(mockData.mittagessen.title)}</h3>
              <p className="text-amber-200 text-lg mb-2">{t(mockData.mittagessen.subtitle)}</p>
              <p className="text-amber-300/70 text-sm">{t(mockData.mittagessen.note)}</p>
            </div>
            
            <div className="space-y-6">
              {Object.entries(mockData.mittagessen.weeklyMenu).map(([day, menus], idx) => (
                <div key={day} className="bg-neutral-900/60 backdrop-blur-sm border border-amber-900/30 rounded-xl p-6 hover:border-amber-600/50 transition-all">
                  <h4 className="text-xl font-semibold text-amber-400 mb-4 capitalize">
                    {language === 'de' 
                      ? ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'][idx]
                      : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'][idx]
                    }
                  </h4>
                  <div className={`grid ${menus.menu2 ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-4`}>
                    <div className="bg-amber-950/30 rounded-lg p-4 border border-amber-900/20">
                      <span className="text-amber-500 font-medium text-sm">{language === 'de' ? 'Menü 1' : 'Menu 1'}</span>
                      <p className="text-amber-100 mt-2">{t(menus.menu1)}</p>
                    </div>
                    {menus.menu2 && (
                      <div className="bg-amber-950/30 rounded-lg p-4 border border-amber-900/20">
                        <span className="text-amber-500 font-medium text-sm">{language === 'de' ? 'Menü 2' : 'Menu 2'}</span>
                        <p className="text-amber-100 mt-2">{t(menus.menu2)}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Star className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-5xl font-serif font-bold text-amber-400 mb-4">{language === 'de' ? 'Kundenbewertungen' : 'Customer Reviews'}</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.reviews.map((review, idx) => (
              <Card key={idx} className="bg-neutral-900/60 backdrop-blur-sm border-amber-900/30 hover:border-amber-600/50 transition-all">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <p className="text-amber-100/80 mb-4 text-sm italic">"{t(review.text)}"</p>
                  <p className="text-amber-400 font-semibold">– {review.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent to-neutral-900/50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <Truck className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-4xl font-serif font-bold text-amber-400 mb-4">{language === 'de' ? 'Lieferservice' : 'Delivery Services'}</h3>
            <p className="text-amber-200/70 text-lg">
              {language === 'de' 
                ? 'Bestellen Sie über unsere Lieferpartner oder direkt bei uns'
                : 'Order through our delivery partners or directly from us'
              }
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {mockData.deliveryPartners.map((partner, idx) => (
              <a
                key={idx}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl p-8 flex items-center justify-center hover:shadow-xl transition-all hover:scale-105 group min-h-32"
              >
                <img 
                  src={partner.logo} 
                  alt={partner.name} 
                  className="max-h-16 w-auto object-contain transition-all"
                />
              </a>
            ))}
          </div>
          <div className="text-center">
            <div className="inline-flex flex-col items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_bremenfoodspot/artifacts/vdw36jk8_image.png"
                alt="Back & Snacks Delivery"
                className="w-48 h-48 object-cover rounded-full shadow-2xl shadow-amber-900/50 border-4 border-amber-600/40"
              />
              <div className="flex items-center gap-3 bg-amber-900/30 backdrop-blur-sm border border-amber-600/40 rounded-full px-6 py-3">
                <Truck className="h-5 w-5 text-amber-400" />
                <span className="text-amber-200">{language === 'de' ? 'Selbstlieferung verfügbar' : 'Own delivery available'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h3 className="text-5xl font-serif font-bold text-amber-400 mb-4">Contact</h3>
            <p className="text-amber-200/70 text-lg">
              {language === 'de' 
                ? 'Besuchen Sie uns oder kontaktieren Sie uns für Bestellungen'
                : 'Visit us or contact us for orders'
              }
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-900/30 rounded-lg">
                  <MapPin className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-amber-300 font-semibold mb-1">{language === 'de' ? 'Adresse' : 'Address'}</h4>
                  <p className="text-amber-100/70">{mockData.contact.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-900/30 rounded-lg">
                  <Phone className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-amber-300 font-semibold mb-1">{language === 'de' ? 'Telefon' : 'Phone'}</h4>
                  <a href={`tel:${mockData.contact.phone}`} className="text-amber-100/70 hover:text-amber-400 transition-colors">
                    {mockData.contact.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-900/30 rounded-lg">
                  <Clock className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-amber-300 font-semibold mb-1">{language === 'de' ? 'Öffnungszeiten' : 'Opening Hours'}</h4>
                  <p className="text-amber-100/70">{language === 'de' ? 'Täglich' : 'Daily'}: {t(mockData.contact.hours)}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-900/30 rounded-lg">
                  <Mail className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-amber-300 font-semibold mb-1">Email</h4>
                  <a href={`mailto:${mockData.contact.email}`} className="text-amber-100/70 hover:text-amber-400 transition-colors">
                    {mockData.contact.email}
                  </a>
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <a 
                  href={mockData.social.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-3 bg-amber-900/30 rounded-lg hover:bg-amber-800/40 transition-all hover:scale-110"
                >
                  <Instagram className="h-6 w-6 text-amber-400" />
                </a>
                <a 
                  href={mockData.social.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-3 bg-amber-900/30 rounded-lg hover:bg-amber-800/40 transition-all hover:scale-110"
                >
                  <Facebook className="h-6 w-6 text-amber-400" />
                </a>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl shadow-amber-900/30 h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2382.5!2d8.781!3d53.063!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTPCsDAzJzQ2LjgiTiA4wrA0Nic1MS42IkU!5e0!3m2!1sen!2sde!4v1234567890!5m2!1sen!2sde"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Back & Snacks Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-neutral-950 border-t border-amber-900/20">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_d541c676-9200-4c85-b009-646509011cd2/artifacts/az877vf0_image.png" 
                alt="Back & Snacks Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <p className="text-amber-400 font-serif font-bold text-lg">Back & Snacks</p>
                <p className="text-amber-300/60 text-xs">Seit 2021</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-amber-200/70 text-sm mb-2">
                {language === 'de' 
                  ? 'Wir freuen uns darauf, Sie bei Back and Snacks begrüßen zu dürfen.'
                  : 'We look forward to welcoming you at Back and Snacks.'
                }
              </p>
              <p className="text-amber-300/50 text-xs">
                {language === 'de' 
                  ? 'Menü ändert sich jede Woche.'
                  : 'Menu changes every week.'
                }
              </p>
            </div>
            <div className="text-amber-300/60 text-sm">
              © 2025 Back & Snacks Bremen
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
